import { House, HouseUserAssociation } from '@/client';
import { Roles } from '@/utils/roleUtils';
import { isDraftHouse, isHouseSetupComplete } from '@/utils/houseUtils';

type HouseUserAssociationWithExpiry = HouseUserAssociation & { expires_at?: string | null };

/**
 * True when the property is a draft the user is still setting up, the user holds the Owner
 * role, or the user is the recorded owner. Contributor and Viewer are guest roles (access
 * granted when a property is shared with the user), so they do not count as ownership.
 */
export const isOwnedProperty = (property: House, userId?: number): boolean => {
	return isDraftHouse(property) || property.user_role?.role === Roles.Owner || property.owner_id === userId;
};

/**
 * True when the property has a non-null original_house_id (an archived clone). This is a fact about
 * the house, independent of who is viewing it — use {@link isOwnedArchiveCopy} / {@link isSharedCopy}
 * when the viewer's relationship to the clone matters (grouping, tile icon).
 */
export const isArchivedProperty = (property: House): boolean => {
	return !!property.original_house_id;
};

/**
 * Whether the viewer can hand this property's ownership on.
 *
 * Only its Owner can: an Admin holds HOUSE_ADMIN, which the route alone would let through, but
 * ownership is not theirs to give. And never on an archive copy: it is a frozen snapshot the
 * professional keeps after transferring the original away, so there is no ownership left on it to
 * transfer and the flow cannot complete.
 *
 * Named as the rule rather than restated at each of the three places that gate on it — the settings
 * section, the access drawer's button, and the route itself. A fourth entrance should ask this
 * question, not rediscover the reasons behind it.
 */
export const canTransferOwnership = (property: House): boolean =>
	property.user_role?.role === Roles.Owner && !isArchivedProperty(property);

/**
 * True when the clone is the current user's own retained copy — the professional keeps the Owner
 * role on it after transferring the original away. This is the viewer-relative sense of "archive":
 * a clone is only *my* archive if I hold it. Drives the archive grouping and archive tile icon.
 */
export const isOwnedArchiveCopy = (property: House, userId?: number): boolean => {
	return isArchivedProperty(property) && isOwnedProperty(property, userId);
};

/**
 * True when the clone belongs to someone else and is shared with the current user (a guest role on
 * another party's retained copy). To the guest it is a shared property that happens to be a frozen
 * snapshot — so it groups under "Shared with Me" with the shared icon, and the snapshot fact is
 * carried by the "(copy)" marker plus a corner badge rather than by swapping the base icon.
 */
export const isSharedCopy = (property: House, userId?: number): boolean => {
	return isArchivedProperty(property) && !isOwnedProperty(property, userId);
};

/**
 * True when the property is shared with the user — any guest role, including a shared clone. Shared
 * clones are not owned by the user, so "not owned by me" fully captures it: a clone I own is my
 * archive (excluded), any clone I don't own is shared with me (included).
 */
export const isSharedWithUser = (property: House, userId?: number): boolean => {
	return !isOwnedProperty(property, userId);
};

/**
 * A House annotated with the viewer-relative flags we derive on the client. `isCopy` is true when the
 * property is an archived clone the current user kept after a professional-to-owner transfer (the
 * original now belongs to the new owner). Computed once at the data boundary (HouseContext) so
 * presentational components read a flag instead of re-deriving it with the user id.
 */
export type ClassifiedHouse = House & { isCopy: boolean };

/** Annotate houses with viewer-relative flags. See {@link ClassifiedHouse}. */
export const classifyHouses = (houses: House[]): ClassifiedHouse[] =>
	houses.map((house) => ({ ...house, isCopy: isArchivedProperty(house) }));

export interface GroupedProperties<T extends House = House> {
	owned: T[];
	sharedWithUser: T[];
	archived: T[];
}

interface GroupPropertiesByOwnershipOptions {
	sortSharedByAccessCreatedAt?: boolean;
}

const isAccessExpired = (property: House): boolean => {
	const expiresAt = (property.user_role as HouseUserAssociationWithExpiry | null | undefined)?.expires_at;
	if (!expiresAt) return false;
	return new Date(expiresAt) <= new Date();
};

const sharedCreatedAt = (property: House): string => property.user_role?.created_at ?? '';

/**
 * Assigns each property to exactly one group. A clone the current user owns is their retained
 * archive copy and is matched first (it also satisfies the plain owned check, since the user keeps
 * the Owner role on it). A clone shared with the user by someone else is not their archive — it
 * falls through to the shared group. The remainder is owned when the user owns it, otherwise shared.
 *
 * A not-yet-onboarded archive is the professional's retention copy mid-build (the finalize job
 * marks it onboarded on completion); it is skipped so a half-built archive never surfaces.
 *
 * Expired shared associations are excluded entirely. The remaining shared properties are
 * sorted by HouseUserAssociation.created_at so that index 0 is always the oldest —
 * matching the backend's free-tier selection for the subscription paywall.
 */
export const groupPropertiesByOwnership = <T extends House>(
	properties: T[],
	userId?: number,
	options: GroupPropertiesByOwnershipOptions = {},
): GroupedProperties<T> => {
	const { sortSharedByAccessCreatedAt = true } = options;
	const owned: T[] = [];
	const sharedWithUser: T[] = [];
	const archived: T[] = [];

	for (const property of properties) {
		if (isOwnedArchiveCopy(property, userId)) {
			if (isHouseSetupComplete(property)) archived.push(property);
			continue;
		}
		if (isOwnedProperty(property, userId)) {
			owned.push(property);
		} else if (!isAccessExpired(property)) {
			sharedWithUser.push(property);
		}
	}

	if (sortSharedByAccessCreatedAt) {
		sharedWithUser.sort((a, b) => sharedCreatedAt(a).localeCompare(sharedCreatedAt(b)));
	}

	return { owned, sharedWithUser, archived };
};
